import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  makeStyles,
  shorthands,
  tokens,
  Title3,
  Text,
  Divider,
  Combobox,
  Option,
  Button,
  Spinner,
  Field,
  Input,
  Checkbox,
  Dropdown,
  SpinButton,
} from "@fluentui/react-components";
import { Save24Regular, ArrowDownload24Regular } from "@fluentui/react-icons";
import { getOrganizationUsers } from "../../services/users.service";
import { MentionUser } from "../discussions/components/richtext/types";
import {
  getSpreadsheetMetadata,
  createSpreadsheetForNewWorkbook,
} from "../../services/spreadsheet-metadata.service";
import {
  getWorkbookSetup,
  saveWorkbookSetup,
  JobType,
  TrialBalancePeriodType,
} from "../../services/spreadsheets.service";
import {
  getConnectedXeroOrgs,
  getTrialBalance,
  XeroOrgSummary,
} from "../../services/xero-integration.service";
import { ExcelService } from "../../services/excel.service";
import {
  XERO_SHEET_NAME,
  XERO_LY_SHEET_NAME,
  XERO_TB_SHEET_NAME,
} from "../../services/non-workpaper-sheets";
import { syncWorksheetsToStatusItems } from "../../services/sync-worksheets.service";
import { createOrUpdateWIIndex } from "../../services/wi-index.service";
import { searchClients, ClientDto } from "../../services/clients.service";
import { applyAnnualsTemplate } from "../../services/job-templates.service";
import { getOfficeManagers, getOfficePartners, OfficeMemberDto } from "../../services/user-role.service";
import { useTypographyStyles } from "../../styles/typography.styles";
import { useNavigate } from "react-router-dom";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";
import { UserRole, canHoldRole, isRoleBelow } from "../../types/UserRole";
import { useMaxRole } from "../../hooks/useMaxRole";
import { useTrackEvent } from "../../hooks/useTrackEvent";
import { isStandaloneMode } from "../../utils/standalone-mode";

/**
 * Job type dropdown options. The `value` is the storage form expected by the
 * backend (snake_case, matches the V044 CHECK constraint and the C# allow-list);
 * the `label` is the human-readable string shown in the UI.
 *
 * "annuals" still triggers the placeholder-document scaffolding on save; the
 * other options are stored only for now and will drive future filtering.
 */
const JOB_TYPE_OPTIONS: readonly { value: JobType; label: string }[] = [
  { value: "annuals", label: "Annuals" },
  { value: "periodic", label: "Periodic" },
  { value: "foodstuffs_annual", label: "Foodstuffs Annual" },
  { value: "foodstuffs_monthly", label: "Foodstuffs Monthly" },
  { value: "other", label: "Other" },
];

const useStyles = makeStyles({
  root: {
    ...shorthands.padding(tokens.spacingVerticalL),
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalS,
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  sectionTitle: {
    display: "block",
  },
  sectionDescription: {
    display: "block",
    marginBottom: tokens.spacingVerticalS,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacingVerticalM,
  },
  /** 70/30 grid — used for Client + Balance Date so the date column stays
   *  narrow (only ever ~10 chars). */
  formGridClientRow: {
    display: "grid",
    gridTemplateColumns: "7fr 3fr",
    gap: tokens.spacingVerticalM,
  },
  fullWidth: {
    gridColumn: "1 / -1",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  successMessage: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
  },
  comboboxListbox: {
    maxHeight: "200px",
  },
});

/**
 * TB period cadence — the enum lives in spreadsheets.service.ts
 * (shared with the WorkbookSetup DTOs). The step size between the
 * "current" balance date and each prior period is decided by this
 * value; how MANY periods to fetch is the separate Period Count
 * spinner (which stores TOTAL periods including current, so its
 * value minus 1 is the "prior periods" count sent to the backend).
 */
const TB_PERIOD_TYPE_OPTIONS: { value: TrialBalancePeriodType; label: string }[] = [
  { value: "annuals",     label: "Annuals / Yearly" },
  { value: "half-yearly", label: "Half-Yearly (6-monthly)" },
  { value: "quarterly",   label: "Quarterly (3-monthly)" },
  { value: "bi-monthly",  label: "Bi-Monthly (2-monthly)" },
  { value: "monthly",     label: "Monthly" },
];

/**
 * Translate the UI's period-type enum into the value the backend Xero
 * TB endpoint's `periodType` query param expects. The enum names
 * (frontend-facing "annuals") and the wire names (backend-facing
 * "years") differ so this indirection stays here rather than shipping
 * the frontend enum on the API.
 */
function tbPeriodTypeToBackendCadence(type: TrialBalancePeriodType): string {
  switch (type) {
    case "monthly":     return "months";
    case "bi-monthly":  return "bi-months";
    case "quarterly":   return "quarters";
    case "half-yearly": return "half-years";
    case "annuals":
    default:            return "years";
  }
}

// Min / default number of PRIOR periods (excluding current) the spinner
// can hold. 0 = current only. Default 1 = current + 1 prior period,
// matching the historical XERO + XERO LY behaviour.
const TB_PERIOD_COUNT_MIN = 0;
const TB_PERIOD_COUNT_DEFAULT = 1;

/**
 * Max PRIOR periods the spinner allows for a given cadence: four years'
 * worth, floored at the historical flat cap of 5 so no existing workbook
 * loses a period (four years of annuals would be 4, one fewer than
 * workbooks could already store).
 *
 * Four years rather than five because the backend fetches each period
 * with its own SEQUENTIAL Xero call, so monthly tops out at 48 prior + 1
 * current = 49 calls — under Xero's 60-per-minute per-tenant limit, which
 * means one import can never trip the limit on its own. Raising these
 * needs that serial loop reworked into a throttled/parallel fetch first.
 *
 * Mirrors SpreadsheetsService.MaxTbPeriodCountFor and
 * XeroIntegrationController.MaxPriorPeriods (which keys off the wire
 * cadence names) on the server. All three move together.
 */
function tbPeriodCountMaxFor(type: TrialBalancePeriodType): number {
  switch (type) {
    case "monthly":     return 48;
    case "bi-monthly":  return 24;
    case "quarterly":   return 16;
    case "half-yearly": return 8;
    case "annuals":
    default:            return 5;
  }
}

/**
 * Workbook configuration form — client, balance date, preparer/reviewer,
 * Xero org. Optionally imports the trial balance to the XERO sheets after save.
 */
const Setup: React.FC = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const navigate = useNavigate();
  const {
    updateWorkbookSetup,
    setSpreadsheetId: setContextSpreadsheetId,
    userInfo,
    userRole,
    setUserRole,
  } = useWorkflowIntelligence();
  const maxRole = useMaxRole();
  const track = useTrackEvent();
  const { enableTrialBalanceImportTypes } = useFeatureFlags();

  // Form state
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClientName, setSelectedClientName] = useState("");
  const [balanceDate, setBalanceDate] = useState("");
  const [preparerEmail, setPreparerEmail] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  // Search/filter state for comboboxes
  const [clientSearch, setClientSearch] = useState("");
  const [preparerSearch, setPreparerSearch] = useState("");
  const [reviewerSearch, setReviewerSearch] = useState("");

  // Client search results
  const [clientOptions, setClientOptions] = useState<ClientDto[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  // Office members (from backend API)
  const [officeManagers, setOfficeManagers] = useState<OfficeMemberDto[]>([]);
  const [officePartners, setOfficePartners] = useState<OfficeMemberDto[]>([]);
  const [isLoadingOfficeMembers, setIsLoadingOfficeMembers] = useState(true);

  // UI state
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [users, setUsers] = useState<MentionUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingSetup, setIsLoadingSetup] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Job type — required dropdown choice. Persisted to the spreadsheets table
  // so it can be used as a filter / report column later. The "annuals" value
  // additionally triggers the placeholder-document scaffolding on save; other
  // values are stored without side-effects.
  const [selectedJobType, setSelectedJobType] = useState<JobType | "">("");
  // Text shown in the Job Type combobox — kept alongside selectedJobType so
  // users can type to filter the list (same UX as the Client and Xero fields).
  const [jobTypeSearch, setJobTypeSearch] = useState<string>("");

  // Xero integration state
  const [xeroOrgs, setXeroOrgs] = useState<XeroOrgSummary[]>([]);
  const [selectedXeroOrg, setSelectedXeroOrg] = useState<string>("");
  const [xeroOrgSearch, setXeroOrgSearch] = useState("");
  const [isLoadingXeroOrgs, setIsLoadingXeroOrgs] = useState(true);
  const [xeroError, setXeroError] = useState<string | null>(null);
  const [savedXeroOrg, setSavedXeroOrg] = useState<string>("");
  const [savedBalanceDate, setSavedBalanceDate] = useState<string>("");
  // Persisted per-workbook — initialised from the loaded setup below.
  // The default (true) covers the fresh-workbook path where there's no
  // stored preference yet.
  const [createWIIndex, setCreateWIIndex] = useState<boolean>(true);

  // Trial balance period cadence + total count (current inclusive).
  // Together they decide the periods the TB import fetches on Save.
  // Persisted per-workbook via tb_period_type + tb_period_count.
  // Controls are gated by enableTrialBalanceImportTypes; when the flag
  // is off, defaults ("annuals" + 2) apply invisibly. Default "annuals"
  // matches the pre-feature behaviour and the DB DEFAULT so users
  // without the flag see no change (legacy rows read back as
  // "annuals" / count=1, preserving today's XERO + XERO LY layout).
  const [tbPeriodType, setTbPeriodType] = useState<TrialBalancePeriodType>("annuals");
  const [tbPeriodCount, setTbPeriodCount] = useState<number>(TB_PERIOD_COUNT_DEFAULT);

  // Trial balance import state
  const [isImportingTb, setIsImportingTb] = useState(false);
  const [tbMessage, setTbMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  /**
   * Bump the caller's role to `target` when they pick themselves out of a
   * preparer/reviewer combobox. Saves the round-trip into Settings that
   * users were doing manually. Conditions:
   *   1. The selected email is the caller's own
   *   2. The caller's max role (per Settings's source of truth) permits
   *      the target role — same gate Settings uses to filter its dropdown
   *   3. Current role is strictly below the target — we never downgrade
   * Runs only on explicit dropdown selection (onOptionSelect), so loading
   * an existing workbook where the user is already the preparer/reviewer
   * doesn't silently change their role.
   *
   * Each rejection path logs why so we can diagnose "this isn't promoting me"
   * reports without guessing — open dev tools, look for [promote-self].
   */
  const maybePromoteSelf = (selectedEmail: string, target: UserRole) => {
    // Prefer mail; fall back to userPrincipalName for tenants where mail
    // isn't synced from Exchange. The preparer/reviewer combobox options
    // also use mail || userPrincipalName, so the two should line up.
    const callerEmail = (userInfo?.mail || userInfo?.userPrincipalName || "").toLowerCase();
    const lowerSelected = selectedEmail.toLowerCase();

    if (!callerEmail) {
      console.debug("[promote-self] no caller email — userInfo not yet loaded?", { userInfo });
      return;
    }
    if (lowerSelected !== callerEmail) {
      console.debug("[promote-self] selected user isn't self", { lowerSelected, callerEmail });
      return;
    }

    if (!canHoldRole(target, maxRole)) {
      console.debug("[promote-self] caller's max role doesn't permit target", { target, maxRole });
      return;
    }

    if (!isRoleBelow(userRole, target)) {
      console.debug("[promote-self] already at target or above", { userRole, target });
      return;
    }

    console.debug("[promote-self] promoting", { from: userRole, to: target });
    setUserRole(target);
  };

  // Debounced client search (filtered by user's office server-side)
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (clientSearch.length < 1) {
        setClientOptions([]);
        return;
      }
      setIsLoadingClients(true);
      try {
        const results = await searchClients(clientSearch, 50);
        setClientOptions(results);
      } catch (error) {
        console.error("Failed to search clients:", error);
      } finally {
        setIsLoadingClients(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearch]);

  // Load users on mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const orgUsers = await getOrganizationUsers();
        setUsers(orgUsers);
      } catch (error) {
        console.error("Failed to load users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  // Load office managers/partners from backend
  useEffect(() => {
    const loadOfficeMembers = async () => {
      try {
        const [managers, partners] = await Promise.all([
          getOfficeManagers(),
          getOfficePartners(),
        ]);
        setOfficeManagers(managers);
        setOfficePartners(partners);
      } catch (error) {
        console.error("Failed to load office members:", error);
      } finally {
        setIsLoadingOfficeMembers(false);
      }
    };
    loadOfficeMembers();
  }, []);

  // Load existing setup on mount. `getSpreadsheetMetadata` returns null when
  // the workbook has no WFI metadata yet (fresh workbook, or one that was just
  // Unlinked). In that case we leave the form blank and let the user fill it
  // in — Save will call `createSpreadsheetForNewWorkbook` at that point.
  useEffect(() => {
    const loadSetup = async () => {
      try {
        const id = await getSpreadsheetMetadata();
        setSpreadsheetId(id);

        if (!id) return;

        const setup = await getWorkbookSetup(id);
        if (setup) {
          setSelectedClientId(setup.clientId || "");
          setSelectedClientName(setup.clientName || "");
          setClientSearch(setup.clientName || "");
          setBalanceDate(setup.balanceDate || "");
          setPreparerEmail(setup.preparerEmail || "");
          setReviewerEmail(setup.reviewerEmail || "");
          setSelectedXeroOrg(setup.xeroTenantId || "");
          setSavedXeroOrg(setup.xeroTenantId || "");
          setSavedBalanceDate(setup.balanceDate || "");
          setXeroOrgSearch(setup.xeroTenantName || "");
          // Legacy rows default to true at the DB layer, so this is only a
          // safety net if the field is somehow absent from the response.
          setCreateWIIndex(setup.createWiIndex ?? true);
          // Same shape as createWiIndex — columns are NOT NULL (default
          // 'annuals' / 2 respectively), so the ?? is a defensive
          // fallback for missing fields on the response wire.
          setTbPeriodType(setup.tbPeriodType ?? "annuals");
          setTbPeriodCount(setup.tbPeriodCount ?? TB_PERIOD_COUNT_DEFAULT);
          // Prefill the dropdown from the saved value. Legacy rows have no
          // jobType — those land on the empty selection, which forces the
          // user to pick something before they can save again.
          if (setup.jobType) {
            setSelectedJobType(setup.jobType);
            const savedLabel = JOB_TYPE_OPTIONS.find((o) => o.value === setup.jobType)?.label;
            if (savedLabel) setJobTypeSearch(savedLabel);
          }
        }
      } catch (error) {
        console.error("Failed to load setup:", error);
      } finally {
        setIsLoadingSetup(false);
      }
    };
    loadSetup();
  }, []);

  // Update search values when users load and we have existing selections
  useEffect(() => {
    if (users.length > 0) {
      if (preparerEmail) {
        const preparer = users.find((u) => u.email === preparerEmail);
        if (preparer) setPreparerSearch(preparer.display);
      }
      if (reviewerEmail) {
        const reviewer = users.find((u) => u.email === reviewerEmail);
        if (reviewer) setReviewerSearch(reviewer.display);
      }
    }
  }, [users, preparerEmail, reviewerEmail]);

  // Load Xero organisations on mount
  useEffect(() => {
    const loadXeroOrgs = async () => {
      try {
        const orgs = await getConnectedXeroOrgs();
        setXeroOrgs(orgs);
        setXeroError(null);
      } catch (error: unknown) {
        console.error("Failed to load Xero organisations:", error);
        // Extract error message from response
        if (error instanceof Error) {
          setXeroError(error.message);
        } else {
          setXeroError("Failed to load Xero organisations");
        }
      } finally {
        setIsLoadingXeroOrgs(false);
      }
    };
    loadXeroOrgs();
  }, []);

  // Substring filter for the Job Type combobox — mirror the Client / Xero
  // combobox behaviour so users can type to narrow the list. When the input
  // matches the currently-selected label exactly (i.e. the user hasn't
  // started editing since selection), show all options so opening the
  // dropdown to change selection shows every choice.
  const filteredJobTypes = useMemo(() => {
    const s = jobTypeSearch.trim().toLowerCase();
    if (!s) return JOB_TYPE_OPTIONS;
    const selectedLabel = JOB_TYPE_OPTIONS.find((o) => o.value === selectedJobType)?.label.toLowerCase();
    if (s === selectedLabel) return JOB_TYPE_OPTIONS;
    return JOB_TYPE_OPTIONS.filter((opt) => opt.label.toLowerCase().includes(s));
  }, [jobTypeSearch, selectedJobType]);

  const handleClientSelect = useCallback((_: unknown, data: { optionValue?: string; optionText?: string }) => {
    const clientId = data.optionValue || "";
    setSelectedClientId(clientId);
    setClientSearch(data.optionText || "");

    const client = clientOptions.find((c) => c.id === clientId);
    const clientName = client?.name || "";
    setSelectedClientName(clientName);

    // Auto-select Xero org if there's an exact name match
    if (clientName) {
      const matchingOrg = xeroOrgs.find(
        (org) => org.orgName?.toLowerCase() === clientName.toLowerCase()
      );
      if (matchingOrg) {
        setSelectedXeroOrg(matchingOrg.tenantId);
        setXeroOrgSearch(matchingOrg.orgName || "");
      }
    }
  }, [clientOptions, xeroOrgs]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Fresh / unlinked workbook path: create the spreadsheet row on the
      // backend and stamp its ID into the workbook now, before saving the
      // form fields. Everything downstream (saveWorkbookSetup, importTrialBalance,
      // applyAnnualsTemplate) needs a real ID. This is the create-on-Save
      // behaviour that replaces the old auto-create-on-init.
      let effectiveSpreadsheetId = spreadsheetId;
      const wasFreshWorkbook = !effectiveSpreadsheetId;
      if (!effectiveSpreadsheetId) {
        effectiveSpreadsheetId = await createSpreadsheetForNewWorkbook();
        setSpreadsheetId(effectiveSpreadsheetId);
      }

      // Look up names from users list
      const preparer = users.find((u) => u.email === preparerEmail);
      const reviewer = users.find((u) => u.email === reviewerEmail);

      const newSetup = {
        clientId: selectedClientId || undefined,
        clientName: selectedClientName || undefined,
        balanceDate: balanceDate || undefined,
        preparerEmail: preparerEmail || undefined,
        preparerName: preparer?.display || undefined,
        reviewerEmail: reviewerEmail || undefined,
        reviewerName: reviewer?.display || undefined,
        xeroTenantId: selectedXeroOrg || undefined,
        xeroTenantName: xeroOrgSearch || undefined,
        // The Save button is disabled when no job type is picked, so we can
        // trust selectedJobType is a real JobType by the time we get here.
        jobType: selectedJobType as JobType,
        createWiIndex: createWIIndex,
        tbPeriodType: tbPeriodType,
        tbPeriodCount: tbPeriodCount,
      };

      await saveWorkbookSetup(effectiveSpreadsheetId, newSetup);
      track("setup.saved", { jobType: selectedJobType });

      // Update context
      setContextSpreadsheetId(effectiveSpreadsheetId);
      updateWorkbookSetup(newSetup);
      setSavedXeroOrg(selectedXeroOrg);
      setSavedBalanceDate(balanceDate);

      if (selectedXeroOrg && balanceDate && !isStandaloneMode()) {
        setSaveMessage({ type: "success", text: "Saved. Importing trial balance..." });
        try {
          await importTrialBalanceToSheets(effectiveSpreadsheetId, tbPeriodType, tbPeriodCount);
          track("xero.trial_balance_imported", {
            tenantId: selectedXeroOrg,
            periodType: tbPeriodType,
            periodCount: tbPeriodCount,
          });
        } catch (tbError) {
          console.error("Failed to import trial balance after save:", tbError);
        }
      }

      // Apply the chosen job's template. Annuals creates the standard set of
      // placeholder documents + linked status items (idempotent — re-runs only
      // create what's missing). Other job types currently have no template;
      // they're just stored. Failures are surfaced but don't roll back the
      // setup save above.
      if (selectedJobType === "annuals") {
        try {
          const result = await applyAnnualsTemplate(effectiveSpreadsheetId);
          const docPart =
            `Created ${result.documentsCreated} placeholder document${result.documentsCreated === 1 ? "" : "s"}`;
          const itemPart = result.statusItemsCreated > 0
            ? ` and ${result.statusItemsCreated} status item${result.statusItemsCreated === 1 ? "" : "s"}`
            : "";
          const skipPart = result.documentsSkipped > 0
            ? `, skipped ${result.documentsSkipped} already present.`
            : ".";
          setSaveMessage({
            type: "success",
            text: `Setup saved. ${docPart}${itemPart}${skipPart}`,
          });
        } catch (templateError) {
          console.error("Failed to apply Annuals template:", templateError);
          setSaveMessage({
            type: "error",
            text: "Setup saved but template creation failed. Try again from Setup.",
          });
          // Don't navigate — let the user see the warning.
          setIsSaving(false);
          return;
        }
      } else {
        setSaveMessage({ type: "success", text: "Setup saved successfully" });
      }

      // Sync status items with the workbook's worksheets so the sign-off
      // certificate (generated from the Review page) picks up every sheet.
      // Users used to forget to hit "Sync Excel" on the Status Items page
      // before signing off, which produced certificates with missing sheet
      // names. Runs after any TB import / template application so sheets
      // those flows create are included in the sync. Failure logs but
      // doesn't roll back the save.
      try {
        await syncWorksheetsToStatusItems(effectiveSpreadsheetId);
      } catch (syncError) {
        console.error("Failed to sync status items after save:", syncError);
      }

      // Refresh the WI_Index sheet (leftmost tab, at-a-glance job
      // headline). Runs last so it can't disrupt the TB import / template
      // scaffold above and so its position=0 assignment wins. Failure
      // logs but doesn't roll back the save — the sheet is a convenience,
      // not authoritative state.
      if (createWIIndex) {
        try {
          await createOrUpdateWIIndex({
            spreadsheetId: effectiveSpreadsheetId,
            clientName: newSetup.clientName,
            balanceDate: newSetup.balanceDate,
            preparerName: newSetup.preparerName,
            reviewerName: newSetup.reviewerName,
          });
        } catch (indexError) {
          console.error("Failed to create WI Index sheet after save:", indexError);
        }
      }

      navigate(`/`);
    } catch (error) {
      console.error("Failed to save setup:", error);
      setSaveMessage({ type: "error", text: "Failed to save setup. Please try again." });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Shared helper: imports trial balance data from Xero and writes it
   * into three sheets:
   *   * XERO     — current period (B:I layout + computed O/Q/R/S formulas
   *                that existing workpapers rely on)
   *   * XERO LY  — first prior period (B:I layout; kept for backward
   *                compatibility even for non-yearly cadences; empty when
   *                priorPeriodCount = 0)
   *   * XERO TB  — every period stacked (long-format, one row per
   *                account per period, with extra Period Label + Period
   *                Date columns so users can pivot / filter as they
   *                like). Only written when the multi-period feature
   *                flag is on — see the gate around the sheet write.
   *
   * `priorPeriodCount` is the number of PRIOR periods to fetch
   * (excluding the current one), matching the backend endpoint's own
   * periodCount semantics — passes straight through with no
   * translation.
   */
  const importTrialBalanceToSheets = async (
    tbSpreadsheetId: string,
    periodType: TrialBalancePeriodType,
    priorPeriodCount: number,
  ) => {
    // Fail fast on a workbook we can't write to. The TB import clears and
    // rewrites whole sheets, so a half-applied run on a read-only workbook
    // is worse than no run at all.
    await ExcelService.assertWorkbookWritable("the trial balance import");

    const backendCadence = tbPeriodTypeToBackendCadence(periodType);
    // Sheet names ("XERO" + "XERO LY") stay the same across modes so
    // workpaper formulas / named ranges keep resolving — the semantics
    // of "prior" change with periodType, not the layout.
    const tb = await getTrialBalance(tbSpreadsheetId, priorPeriodCount, backendCadence);

    // Shared 8-col header + row mapper for XERO / XERO LY. `dateOverride`
    // lets XERO LY show its own period's balance date (the top-level
    // `tb.balanceDate` is the current period; comparison periods carry
    // their own date on `ComparisonPeriods[i].date`).
    const header: (string | number)[] = ["Account Code", "Account Name", "Account Type", "Debit", "Credit", "Date", "Organisation", "Balance"];
    const mapRows = (rows: typeof tb.rows, dateOverride?: string) =>
      rows.map((row) => [
        row.accountCode,
        row.accountName,
        row.accountType,
        row.debit,
        row.credit,
        dateOverride ?? tb.balanceDate ?? "",
        tb.organisationName,
        row.balance,
      ]);

    const dataRows = mapRows(tb.rows);

    // --- XERO sheet: current period ---
    // Clear any stale rows from a previous import — without this, leftover
    // accounts past the new data length would remain.
    await ExcelService.getOrCreateWorksheet(XERO_SHEET_NAME);
    await ExcelService.clearWorksheetContents(XERO_SHEET_NAME);
    await ExcelService.insertRange([header, ...dataRows], "B1", XERO_SHEET_NAME);
    await ExcelService.formatRange("B1:I1", { bold: true }, XERO_SHEET_NAME);

    // --- XERO LY sheet: first prior period (empty if periodCount = 1) ---
    const firstPrior = tb.comparisonPeriods[0];
    const priorYearRows = firstPrior
      ? mapRows(firstPrior.rows, firstPrior.date ?? undefined)
      : [];
    await ExcelService.getOrCreateWorksheet(XERO_LY_SHEET_NAME);
    await ExcelService.clearWorksheetContents(XERO_LY_SHEET_NAME);
    await ExcelService.insertRange([header, ...priorYearRows], "B1", XERO_LY_SHEET_NAME);
    await ExcelService.formatRange("B1:I1", { bold: true }, XERO_LY_SHEET_NAME);

    // --- Computed formula columns on XERO sheet (O, Q, R, S) ---
    // Row 1 = header, data starts at row 2
    if (dataRows.length > 0) {
      const formulas: string[][] = dataRows.map((_, i) => {
        const r = i + 2; // Excel row number (1-indexed, skip header)
        return [
          // O: Clean account code — convert locale decimal separators to "." then parse as number
          `=IFERROR(IF(B${r}="","",VALUE(SUBSTITUTE(CLEAN(B${r}),CLEAN(separator),"."))),B${r})`,
          // P: empty (spacer)
          "",
          // Q: Filtered account name
          `=IF(B${r}="","",C${r})`,
          // R: This year balance (rounded)
          `=IF(B${r}="","",ROUND(I${r},rounding))`,
          // S: Last year balance (VLOOKUP from XERO LY)
          `=VLOOKUP(B${r},lyxero,8,FALSE)`,
        ];
      });
      await ExcelService.setFormulas(formulas, "O2", XERO_SHEET_NAME);
    }

    // --- XERO TB sheet: every period stacked (long-format) ---
    // Gated behind the same feature flag as the Period Type / Count
    // controls — flag-off workbooks see exactly the historical two
    // sheets (XERO + XERO LY). New workpapers that rely on XERO TB
    // must therefore also require the flag; that's fine because the
    // multi-period sheet is only useful when the multi-period UI is
    // available anyway.
    //
    // 10-col header — same 8 cols as XERO/XERO LY plus Period Label
    // (PY0/PY1/…) and Period Date so users can sort / pivot on either.
    // Current period is PY0 (labelled to make row ordering obvious);
    // prior periods land as PY1..PYn in the order the backend returns.
    // Always written when the flag is on (even at priorPeriodCount = 0)
    // so flag-on workpapers can rely on this sheet existing.
    if (enableTrialBalanceImportTypes) {
      const tbHeader: (string | number)[] = [
        "Period Label", "Period Date", "Account Code", "Account Name", "Account Type", "Debit", "Credit", "Date", "Organisation", "Balance",
      ];
      const periods: { label: string; date: string; rows: typeof tb.rows }[] = [
        { label: "PY0", date: tb.balanceDate ?? "", rows: tb.rows },
        ...tb.comparisonPeriods.map((cp) => ({
          label: cp.label,
          date: cp.date ?? "",
          rows: cp.rows,
        })),
      ];
      const tbDataRows: (string | number)[][] = periods.flatMap((period) =>
        mapRows(period.rows, period.date).map((row) => [period.label, period.date, ...row]),
      );
      await ExcelService.getOrCreateWorksheet(XERO_TB_SHEET_NAME);
      await ExcelService.clearWorksheetContents(XERO_TB_SHEET_NAME);
      await ExcelService.insertRange([tbHeader, ...tbDataRows], "B1", XERO_TB_SHEET_NAME);
      await ExcelService.formatRange("B1:K1", { bold: true }, XERO_TB_SHEET_NAME);
    }

    // Persist all of the imported sheets + formulas so they aren't lost if the
    // user closes without saving.
    await ExcelService.save();

    return tb;
  };

  const handleImportTrialBalance = async () => {
    if (!spreadsheetId || !selectedXeroOrg) return;

    setIsImportingTb(true);
    setTbMessage(null);

    try {
      const tb = await importTrialBalanceToSheets(spreadsheetId, tbPeriodType, tbPeriodCount);
      setTbMessage({ type: "success", text: `Imported ${tb.rows.length} accounts to XERO sheet` });
    } catch (error) {
      console.error("Failed to import trial balance:", error);
      const message = error instanceof Error ? error.message : "Failed to import trial balance";
      setTbMessage({ type: "error", text: message });
    } finally {
      setIsImportingTb(false);
    }
  };

  if (isLoadingSetup) {
    return (
      <div className={styles.root}>
        <Spinner label="Loading setup..." />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Title3>Workbook Setup</Title3>
        <Text className={typography.bodyLarge}>
          Configure the client details and team members for this workbook.
        </Text>
      </div>

      <Divider />

      <div className={styles.section}>
        <Text className={`${typography.pageTitle} ${styles.sectionTitle}`}>Client Information</Text>
        <Text className={`${typography.caption} ${styles.sectionDescription}`}>
          Search and select the client for this workbook
        </Text>

        <div className={styles.formGridClientRow}>
          <Field label="Client" required>
            <Combobox
              value={clientSearch}
              selectedOptions={selectedClientId ? [selectedClientId] : []}
              onOptionSelect={handleClientSelect}
              onChange={(e) => {
                setClientSearch(e.target.value);
                // Clear selection if user edits the text
                if (selectedClientId) {
                  setSelectedClientId("");
                  setSelectedClientName("");
                }
              }}
              placeholder="Search for a client..."
              freeform
              inlinePopup
              listbox={{ className: styles.comboboxListbox }}
              // Suppress the WebView's built-in autofill dropdown so it doesn't
              // overlay our own suggestions list. "off" is sometimes ignored by
              // Chromium; if it stops working we can switch to "new-password" —
              // but that triggers password-manager prompts so it's plan B.
              autoComplete="off"
            >
              {isLoadingClients ? (
                <Option key="loading" value="" text="" disabled>
                  Loading...
                </Option>
              ) : clientOptions.length === 0 && clientSearch.length >= 1 ? (
                <Option key="no-results" value="" text="" disabled>
                  No clients found
                </Option>
              ) : (
                clientOptions.map((client) => (
                  <Option key={client.id} value={client.id} text={client.name}>
                    {client.name}
                  </Option>
                ))
              )}
            </Combobox>
          </Field>

          <Field label="Balance Date" required>
            <Input
              type="date"
              value={balanceDate}
              onChange={(_, data) => setBalanceDate(data.value)}
            />
          </Field>
        </div>
      </div>

      <Divider />

      <div className={styles.section}>
        <Text className={`${typography.pageTitle} ${styles.sectionTitle}`}>Team Members</Text>
        <Text className={`${typography.caption} ${styles.sectionDescription}`}>
          Assign the preparer and reviewer for this workbook
        </Text>

        <div className={styles.formGrid}>
          <Field label="Preparer" required>
            <Combobox
              value={preparerSearch}
              selectedOptions={preparerEmail ? [preparerEmail] : []}
              onOptionSelect={(_, data) => {
                const selectedEmail = data.optionValue || "";
                setPreparerEmail(selectedEmail);
                setPreparerSearch(data.optionText || "");
                maybePromoteSelf(selectedEmail, UserRole.Preparer);
              }}
              onChange={(e) => setPreparerSearch(e.target.value)}
              placeholder={isLoadingUsers ? "Loading users..." : "Select preparer"}
              disabled={isLoadingUsers}
              freeform
              inlinePopup
              listbox={{ className: styles.comboboxListbox }}
              autoComplete="off"
            >
              {users
                .filter((user) =>
                  user.display.toLowerCase().includes(preparerSearch.toLowerCase()) ||
                  (user.email ?? "").toLowerCase().includes(preparerSearch.toLowerCase())
                )
                .map((user) => (
                  <Option key={user.email} value={user.email} text={user.display}>
                    {user.display}
                  </Option>
                ))}
            </Combobox>
          </Field>

          <Field label="Reviewer" required>
            <Combobox
              value={reviewerSearch}
              selectedOptions={reviewerEmail ? [reviewerEmail] : []}
              onOptionSelect={(_, data) => {
                const selectedEmail = data.optionValue || "";
                setReviewerEmail(selectedEmail);
                setReviewerSearch(data.optionText || "");
                maybePromoteSelf(selectedEmail, UserRole.Reviewer);
              }}
              onChange={(e) => setReviewerSearch(e.target.value)}
              placeholder={isLoadingUsers ? "Loading users..." : "Select reviewer"}
              disabled={isLoadingUsers}
              freeform
              inlinePopup
              listbox={{ className: styles.comboboxListbox }}
              autoComplete="off"
            >
              {users
                .filter((user) =>
                  user.display.toLowerCase().includes(reviewerSearch.toLowerCase()) ||
                  (user.email ?? "").toLowerCase().includes(reviewerSearch.toLowerCase())
                )
                .map((user) => (
                  <Option key={user.email} value={user.email} text={user.display}>
                    {user.display}
                  </Option>
                ))}
            </Combobox>
          </Field>
        </div>
      </div>

      <Divider />

      <div className={styles.section}>
        <Text className={`${typography.pageTitle} ${styles.sectionTitle}`}>Xero Integration</Text>
        <Text className={`${typography.caption} ${styles.sectionDescription}`}>
          Link this workbook to a Xero organisation for data integration
        </Text>

        <div className={styles.formGrid}>
          <Field label="Xero Organisation" className={styles.fullWidth}>
            {xeroError ? (
              <Text className={styles.errorMessage}>{xeroError}</Text>
            ) : (
              <Combobox
                value={xeroOrgSearch}
                selectedOptions={selectedXeroOrg ? [selectedXeroOrg] : []}
                onOptionSelect={(_, data) => {
                  setSelectedXeroOrg(data.optionValue || "");
                  setXeroOrgSearch(data.optionText || "");
                }}
                onChange={(e) => setXeroOrgSearch(e.target.value)}
                placeholder={isLoadingXeroOrgs ? "Loading organisations..." : "Select Xero organisation"}
                disabled={isLoadingXeroOrgs}
                freeform
                inlinePopup
                listbox={{ className: styles.comboboxListbox }}
                autoComplete="off"
              >
                {[...xeroOrgs]
                  .sort((a, b) => (a.orgName || "").localeCompare(b.orgName || ""))
                  .filter((org) =>
                    org.orgName?.toLowerCase().includes(xeroOrgSearch.toLowerCase())
                  )
                  .map((org) => (
                    <Option key={org.tenantId} value={org.tenantId} text={org.orgName || ""}>
                      {org.orgName}
                    </Option>
                  ))}
              </Combobox>
            )}
          </Field>

          {enableTrialBalanceImportTypes && (
            // Period type + count sit side-by-side in the setup form's
            // 2-column grid (7fr : 3fr) — the wider column gives the
            // dropdown room for the longest label ("Half-Yearly") plus
            // its chevron; the narrower column suits the spinner's
            // fixed-width value + up/down buttons.
            <>
              <Field label="Trial balance period">
                {/* If the stored value isn't in the current option list
                    (empty, or a legacy value that no longer exists),
                    the dropdown falls back to Annuals for display + as
                    the effective selection — safer than showing a
                    blank trigger the user can't easily recover from. */}
                {(() => {
                  const matched = TB_PERIOD_TYPE_OPTIONS.find((o) => o.value === tbPeriodType);
                  const displayValue = matched ? matched.label : TB_PERIOD_TYPE_OPTIONS[0].label;
                  const selected = matched ? matched.value : TB_PERIOD_TYPE_OPTIONS[0].value;
                  return (
                    <Dropdown
                      value={displayValue}
                      selectedOptions={[selected]}
                      onOptionSelect={(_, data) => {
                        if (!data.optionValue) return;
                        const nextType = data.optionValue as TrialBalancePeriodType;
                        setTbPeriodType(nextType);
                        // Switching to a cadence with a lower cap (e.g. 48
                        // monthly → annuals, max 5) would otherwise leave the
                        // spinner showing a value the backend rejects on save.
                        setTbPeriodCount((c) => Math.min(c, tbPeriodCountMaxFor(nextType)));
                      }}
                    >
                      {TB_PERIOD_TYPE_OPTIONS.map((opt) => (
                        <Option key={opt.value} value={opt.value} text={opt.label}>
                          {opt.label}
                        </Option>
                      ))}
                    </Dropdown>
                  );
                })()}
              </Field>
              <Field label={`Number of previous periods to include (max ${tbPeriodCountMaxFor(tbPeriodType)})`}>
                <SpinButton
                  value={tbPeriodCount}
                  min={TB_PERIOD_COUNT_MIN}
                  max={tbPeriodCountMaxFor(tbPeriodType)}
                  onChange={(_, data) => {
                    // SpinButton delivers either `value` (button click) or
                    // `displayValue` (typed input); clamp both back into
                    // [MIN, MAX] and fall back to the current state when
                    // the user's typing yields NaN mid-edit.
                    let next: number | null | undefined = data.value;
                    if (next == null && data.displayValue != null) {
                      const parsed = parseInt(data.displayValue, 10);
                      next = Number.isFinite(parsed) ? parsed : undefined;
                    }
                    if (next == null || !Number.isFinite(next)) return;
                    setTbPeriodCount(
                      Math.max(TB_PERIOD_COUNT_MIN, Math.min(tbPeriodCountMaxFor(tbPeriodType), next)),
                    );
                  }}
                />
              </Field>
            </>
          )}

          {!isStandaloneMode() && (
            <div className={styles.fullWidth}>
              <Button
                appearance="secondary"
                icon={<ArrowDownload24Regular />}
                onClick={handleImportTrialBalance}
                disabled={!selectedXeroOrg || !spreadsheetId || isImportingTb || selectedXeroOrg !== savedXeroOrg || !balanceDate || balanceDate !== savedBalanceDate}
              >
                {isImportingTb ? "Importing..." : "Import Trial Balance"}
              </Button>
              {!balanceDate && (
                <Text className={typography.bodySmall} style={{ marginLeft: "8px", color: tokens.colorNeutralForeground3 }}>
                  Set a balance date above before importing
                </Text>
              )}
              {balanceDate && balanceDate !== savedBalanceDate && (
                <Text className={typography.bodySmall} style={{ marginLeft: "8px", color: tokens.colorNeutralForeground3 }}>
                  Save changes before importing
                </Text>
              )}
              {tbMessage && (
                <Text
                  className={`${typography.bodySmall} ${tbMessage.type === "success" ? styles.successMessage : styles.errorMessage}`}
                  style={{ marginLeft: "8px" }}
                >
                  {tbMessage.text}
                </Text>
              )}
            </div>
          )}
        </div>
      </div>

      <Divider />

      <div className={styles.section}>
        <Text className={`${typography.pageTitle} ${styles.sectionTitle}`}>Job Type</Text>
        <Text className={`${typography.caption} ${styles.sectionDescription}`}>
          Pick the type of job this workbook is for. Annuals also scaffolds the
          standard set of placeholder documents on save.
        </Text>

        <Field label="Job Type" required>
          <Combobox
            placeholder="Select or search a job type"
            value={jobTypeSearch}
            selectedOptions={selectedJobType ? [selectedJobType] : []}
            onOptionSelect={(_e, data) => {
              if (data.optionValue) {
                const value = data.optionValue as JobType;
                setSelectedJobType(value);
                const label = JOB_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? "";
                setJobTypeSearch(label);
              }
            }}
            onChange={(e) => setJobTypeSearch(e.target.value)}
            onKeyDown={(e) => {
              // Belt-and-braces Tab-to-confirm. Fluent Combobox v9's built-in
              // Tab-select via activeDescendant doesn't fire reliably in this
              // form — most likely related to active-descendant id churn when
              // our substring filter narrows the option list to a single
              // entry between renders. Manually confirm the first filtered
              // option here. Fluent's own Tab handler runs BEFORE this via
              // mergeCallbacks so this is a no-op when Fluent already picked.
              if (e.key !== "Tab" || e.shiftKey) return;
              const first = filteredJobTypes[0];
              if (!first) return;
              if (selectedJobType === first.value) return;
              setSelectedJobType(first.value);
              setJobTypeSearch(first.label);
            }}
            inlinePopup
            // Same rationale as the Client combobox above — suppress the
            // WebView autofill so it doesn't overlay our own suggestions.
            autoComplete="off"
          >
            {filteredJobTypes.length === 0 ? (
              <Option key="no-results" value="" text="" disabled>
                No matching job types
              </Option>
            ) : (
              filteredJobTypes.map((opt) => (
                <Option key={opt.value} value={opt.value} text={opt.label}>
                  {opt.label}
                </Option>
              ))
            )}
          </Combobox>
        </Field>
      </div>

      <Divider />

      <div className={styles.footer}>
        {/* Save message rendered before the checkbox so its appear /
            disappear only shifts space on the left; the checkbox and
            Save button stay glued to the right edge of the flex-end
            footer. */}
        {saveMessage && (
          <Text className={`${typography.bodySmall} ${saveMessage.type === "success" ? styles.successMessage : styles.errorMessage}`}>
            {saveMessage.text}
          </Text>
        )}
        <Checkbox
          label="Create WI Index sheet"
          checked={createWIIndex}
          onChange={(_e, data) => setCreateWIIndex(!!data.checked)}
        />
        <Button
          appearance="primary"
          icon={<Save24Regular />}
          onClick={handleSave}
          disabled={
            isSaving
            || selectedJobType === ""
            || !selectedClientId
            || !balanceDate
            || !preparerEmail
            || !reviewerEmail
          }
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default Setup;
